import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { TextChain } from '@/api/types/stowed-element'
import { expandMirrorSelectionMembers } from '@/lib/groups/mirror-group-expansion'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  remapClipboardParentGroupId,
  shapeSnapshotNestingDepth,
} from '@/lib/groups/clipboard-group-policy'
import {
  buildMirrorCopyShapeData,
  isShapeMirrorCopyEligible,
} from '@/lib/transforms/mirror-shape-across-spine'
import { readLiveSelection } from '../helpers/live-selection'
import type { LayoutPanelProps } from '../types/layout-panel-props'

type MirrorCopyContext = {
  mgr: ShapeManagerRef | null | undefined
  mirrorSpineAxisX: number | null | undefined
  textChains: TextChain[]
  onTextFramesLinked: LayoutPanelProps['onTextFramesLinked']
  onMirrorMoveHoverChange: LayoutPanelProps['onMirrorMoveHoverChange']
}

/** Mirror Copy: clones the live selection (and every group descendant) mirrored across the
 *  spine, relinks cloned text-chain members, and selects the new top-level clones. */
export function handleMirrorCopy({
  mgr,
  mirrorSpineAxisX,
  textChains,
  onTextFramesLinked,
  onMirrorMoveHoverChange,
}: MirrorCopyContext): void {
  if (mirrorSpineAxisX == null) return

  const { selectedShapes, allShapes } = readLiveSelection(mgr)

  if (selectedShapes.length === 0) return

  const shapesById = new Map(allShapes.map((s) => [s.id, s]))

  if (!selectedShapes.every((s) => isShapeMirrorCopyEligible(s, shapesById, []))) {
    return
  }

  const historyTxnId = crypto.randomUUID()

  // Fold every selected group's descendants (any nesting depth) into the flat clone list below.
  const { shapes: shapesToClone } = expandMirrorSelectionMembers(selectedShapes, [], allShapes, [])
  const topLevelShapeIds = new Set(selectedShapes.map((s) => s.id))

  // Every shape id involved is assigned its new id up front so a descendant's `parentGroupId`
  // can be remapped to the *new* parent id on its very first write, and so a group shell is
  // only created once every descendant it needs already exists — the identical pattern
  // `pasteFromClipboard` (center-canvas/hooks/use-clipboard-paste.ts) uses for cloning a group hierarchy.
  const sourceIdToPastedId = new Map<string, string>()

  for (const shape of shapesToClone) {
    // Backend requires a 32-character hex GUID (no dashes) for client-generated shape ids —
    // matches `ShapeManagerRef`'s own `generateShapeId` (shape-manager/hooks/use-shape-collection.ts).
    sourceIdToPastedId.set(shape.id, crypto.randomUUID().replace(/-/g, ''))
  }

  const newShapeIds: string[] = []

  if (mgr && shapesToClone.length > 0) {
    // A group shell must be created only after every descendant it needs already exists —
    // sort deepest-nested first so an outer group is never created before an inner group/leaf
    // it contains.
    const shapesToCloneById = new Map(shapesToClone.map((s) => [s.id, s]))
    const orderedShapes = [...shapesToClone].sort(
      (a, b) =>
        shapeSnapshotNestingDepth(b, shapesToCloneById) -
        shapeSnapshotNestingDepth(a, shapesToCloneById)
    )

    for (const shape of orderedShapes) {
      const clone: ShapeData = {
        ...buildMirrorCopyShapeData(shape, mirrorSpineAxisX),
        id: sourceIdToPastedId.get(shape.id)!,
        parentGroupId: remapClipboardParentGroupId(shape.parentGroupId, sourceIdToPastedId),
      }

      const created = mgr.addShapeData(clone, {
        selectAfterCreate: false,
        meta: {
          historyTxnId,
          ...(isGroupShape(clone) ? { isGroupCreation: true } : {}),
        },
      })

      if (topLevelShapeIds.has(shape.id)) {
        newShapeIds.push(created.id)
      }
    }
  }

  // Relink cloned chain members to each other: when both halves of a text-linking pair (e.g.
  // a "+"-linked overflow frame) are cloned in the same Mirror Copy gesture, the clones should
  // stay linked too instead of becoming two independent, disconnected copies. Only relinks a
  // pair when BOTH members were cloned this gesture — a chain partner that wasn't selected
  // stays unlinked in its clone, same as before (an orphan copy is the correct, safe outcome
  // there, matching `buildMirrorCopyShapeData` clearing `uid`).
  if (onTextFramesLinked && textChains.length > 0) {
    relinkClonedTextChains(
      shapesToClone,
      sourceIdToPastedId,
      textChains,
      onTextFramesLinked,
      historyTxnId
    )
  }

  if (newShapeIds.length > 0) {
    mgr?.selectShapesWithHistory(newShapeIds, { historyTxnId })
  }

  onMirrorMoveHoverChange?.(false)
}

function relinkClonedTextChains(
  shapesToClone: ShapeData[],
  sourceIdToPastedId: Map<string, string>,
  textChains: TextChain[],
  onTextFramesLinked: NonNullable<LayoutPanelProps['onTextFramesLinked']>,
  historyTxnId: string
): void {
  const originalUidToId = new Map<string, string>()

  // One loop, not two: `dev` walked a separate `textBoxesToClone` list here because a chain
  // member could be a TextManager box. Every chain member is a shape now, so `shapesToClone`
  // already contains all of them (text-unification Phase 3).
  for (const shape of shapesToClone) {
    if (shape.uid != null) originalUidToId.set(String(shape.uid), shape.id)
  }

  // For a chain of 3+ cloned members (A-B-C), this fires once per consecutive pair (A-B, then
  // B-C) in the same synchronous pass. `home.tsx`'s `textChains` state doesn't advance
  // mid-handler, so each call must be threaded against the PREVIOUS call's result — not the
  // shared `textChains` snapshot — or the second call computes its link against a "before"
  // that doesn't know about the first, and only the last link survives (WMPCM-9233 follow-up).
  let runningChains: TextChain[] = textChains

  for (const chain of textChains) {
    for (let i = 0; i < chain.textBox.length - 1; i++) {
      const sourceOriginalId = originalUidToId.get(chain.textBox[i]!.uid)
      const targetOriginalId = originalUidToId.get(chain.textBox[i + 1]!.uid)

      if (!sourceOriginalId || !targetOriginalId) continue

      const clonedSourceId = sourceIdToPastedId.get(sourceOriginalId)
      const clonedTargetId = sourceIdToPastedId.get(targetOriginalId)

      if (clonedSourceId && clonedTargetId) {
        // Share this gesture's historyTxnId so the link (and its reflow persistence) joins
        // the clone-creation actions as one compound undo step, instead of opening a second,
        // separately-undoable step right after.
        const chainsAfter = onTextFramesLinked(
          clonedSourceId,
          clonedTargetId,
          historyTxnId,
          runningChains
        )

        if (chainsAfter) runningChains = chainsAfter
      }
    }
  }
}
